import * as Yup from 'yup';
import format from 'date-fns/format';
import * as languages from 'date-fns/locale';
import subYears from 'date-fns/subYears';
import 'yup-phone-lite';

import { extractPhoneParts } from '@utils/phone';

import {
  INVALID_EMAIL_ERROR,
  REQUIRED_FIELD_ERROR,
  INVALID_DATE_ERROR,
  INVALID_FOUR_ZIPCODE_ERROR,
  INVALID_FIVE_ZIPCODE_ERROR,
  INVALID_ID_NUMBER_ERROR,
  INVALID_PHONE_NUMBER,
  INVALID_COUNTRY_ERROR,
} from '@common/constants/formValidation';
import { LANGUAGE_DE } from '@common/constants/languages';
import { MOBILE_CONTRACT_ID_MAP } from '@common/constants/mobileContractModels';
import {
  IDENTITY_DOCUMENTS_TYPES,
  getIdentityDocumentsTypes,
} from '@common/constants/identityDocuments';
import {
  COUNTRIES,
  COUNTRY_GERMANY,
  COUNTRY_FRANCE,
  COUNTRY_ITALY,
  COUNTRY_SWITZERLAND,
  COUNTRY_PHONE_PREFIX_SWITZERLAND,
  PHONE_PREFIX_TO_COUNTRY_MAP,
} from '@common/constants/countries';
import { getAllCountriesOptions } from '@common/constants/allCountries';

export const MOBILE_ORDER_FORM_STATE = 'MOBILE_ORDER_FORM_STATE';
export const MOBILE_ORDER_FORM_CURRENT_STEP = 'MOBILE_ORDER_FORM_CURRENT_STEP';

export const initialValues = {
  mobile_plan: '',
  existing_phone_number: COUNTRY_PHONE_PREFIX_SWITZERLAND,
  is_existing_number: '',
  current_contract_model: '',
  current_provider: '',
  gender: '1',
  first_name: '',
  last_name: '',
  date_of_birth: '',
  nationality: '',
  id_type: '',
  id_number: '',
  email: '',
  contact_phone_number: '+41',
  delivery_address: {
    country: '',
    postal_code: '',
    place: '',
    street: '',
    house_nr: '',
    suffix: '',
    noAdditionalStreetNumber: false,
  },
  billing_address: {
    gender: '',
    first_name: '',
    last_name: '',
    country: '',
    postal_code: '',
    place: '',
    street: '',
    house_nr: '',
    suffix: '',
    should_fill_with_delivery_data: true,
    noAdditionalStreetNumber: false,
  },
  roaming: 'agree',
  terms_and_conditions_salt: false,
  port_phone_number: false,
};

const identityDocuments = getIdentityDocumentsTypes(() => {});
const allCountriesValues = getAllCountriesOptions(() => {}).map(
  country => country.value,
);

const today = new Date();
const minDate = subYears(today, 110);

export const validationSchema = Yup.object().shape({
  mobile_plan: Yup.number().required(REQUIRED_FIELD_ERROR),
  is_existing_number: Yup.string()
    .oneOf(['yes', 'no'])
    .required(REQUIRED_FIELD_ERROR),
  current_contract_model: Yup.string().when('is_existing_number', {
    is: is_existing_number => is_existing_number === 'yes',
    then: Yup.string().required(REQUIRED_FIELD_ERROR),
    otherwise: Yup.string(),
  }),
  current_provider: Yup.string().when('is_existing_number', {
    is: is_existing_number => is_existing_number === 'yes',
    then: Yup.string().required(REQUIRED_FIELD_ERROR),
    otherwise: Yup.string(),
  }),
  email: Yup.string().email(INVALID_EMAIL_ERROR).required(REQUIRED_FIELD_ERROR),
  existing_phone_number: Yup.string().when('is_existing_number', {
    is: is_existing_number => is_existing_number === 'yes',
    otherwise: Yup.string(),
    then: Yup.string()
      .phone(COUNTRY_SWITZERLAND, INVALID_PHONE_NUMBER)
      .required(REQUIRED_FIELD_ERROR),
  }),
  first_name: Yup.string().required(REQUIRED_FIELD_ERROR),
  last_name: Yup.string().required(REQUIRED_FIELD_ERROR),
  date_of_birth: Yup.string()
    .length(10, INVALID_DATE_ERROR)
    .required(REQUIRED_FIELD_ERROR)
    .min(minDate, INVALID_DATE_ERROR)
    .max(today, INVALID_DATE_ERROR)
    .nullable(),
  nationality: Yup.string()
    .oneOf(allCountriesValues, INVALID_COUNTRY_ERROR)
    .required(REQUIRED_FIELD_ERROR),
  id_number: Yup.string()
    .when(
      'id_type',
      (id_type, schema) =>
        id_type &&
        schema.test({
          message: INVALID_ID_NUMBER_ERROR,
          name: 'invalidIdNumber',
          test: value =>
            identityDocuments
              .find(item => item.value === id_type)
              .reqexp.test(value),
        }),
    )
    .required(REQUIRED_FIELD_ERROR),
  id_type: Yup.string()
    .oneOf(IDENTITY_DOCUMENTS_TYPES)
    .required(REQUIRED_FIELD_ERROR),
  gender: Yup.string().required(REQUIRED_FIELD_ERROR),
  contact_phone_number: Yup.string().when(
    'contact_phone_number_prefix',
    (contactPhoneNumber, schema) =>
      schema
        .phone(
          PHONE_PREFIX_TO_COUNTRY_MAP[contactPhoneNumber],
          INVALID_PHONE_NUMBER,
        )
        .required(REQUIRED_FIELD_ERROR),
  ),
  delivery_address: Yup.object().shape({
    noAdditionalStreetNumber: Yup.boolean().default(false),
    country: Yup.string().oneOf(COUNTRIES).required(REQUIRED_FIELD_ERROR),
    postal_code: Yup.string()
      .required(REQUIRED_FIELD_ERROR)
      .when('country', {
        is: country =>
          [COUNTRY_GERMANY, COUNTRY_FRANCE, COUNTRY_ITALY].includes(country),
        otherwise: Yup.string().test({
          message: INVALID_FOUR_ZIPCODE_ERROR,
          name: 'zipCodeLengthFour',
          test: value => value && value.length === 4,
        }),
        then: Yup.string().test({
          message: INVALID_FIVE_ZIPCODE_ERROR,
          name: 'zipCodeLengthFive',
          test: value => value && value.length === 5,
        }),
      }),
    place: Yup.string().required(REQUIRED_FIELD_ERROR),
    street: Yup.string().required(REQUIRED_FIELD_ERROR),
    house_nr: Yup.string().when('noAdditionalStreetNumber', {
      is: false,
      then: Yup.string().required(REQUIRED_FIELD_ERROR),
      otherwise: Yup.string(),
    }),
    suffix: Yup.string(),
  }),
  billing_address: Yup.object().shape({
    noAdditionalStreetNumber: Yup.boolean().default(false),
    first_name: Yup.string().when('should_fill_with_delivery_data', {
      is: shouldFillWithData => shouldFillWithData === true,
      then: Yup.string(),
      otherwise: Yup.string().required(REQUIRED_FIELD_ERROR),
    }),
    last_name: Yup.string().when('should_fill_with_delivery_data', {
      is: shouldFillWithData => shouldFillWithData === true,
      then: Yup.string(),
      otherwise: Yup.string().required(REQUIRED_FIELD_ERROR),
    }),
    gender: Yup.string().when('should_fill_with_delivery_data', {
      is: shouldFillWithData => shouldFillWithData === true,
      then: Yup.string(),
      otherwise: Yup.string().required(REQUIRED_FIELD_ERROR),
    }),
    country: Yup.string().when('should_fill_with_delivery_data', {
      is: shouldFillWithData => shouldFillWithData === true,
      then: Yup.string(),
      otherwise: Yup.string().oneOf(COUNTRIES).required(REQUIRED_FIELD_ERROR),
    }),
    postal_code: Yup.string().when('should_fill_with_delivery_data', {
      is: shouldFillWithData => shouldFillWithData === true,
      then: Yup.string(),
      otherwise: Yup.string()
        .when('country', {
          is: country =>
            [COUNTRY_GERMANY, COUNTRY_FRANCE, COUNTRY_ITALY].includes(country),
          otherwise: Yup.string().test({
            message: INVALID_FOUR_ZIPCODE_ERROR,
            name: 'zipCodeLengthFour',
            test: value => value && value.length === 4,
          }),
          then: Yup.string().test({
            message: INVALID_FIVE_ZIPCODE_ERROR,
            name: 'zipCodeLengthFive',
            test: value => value && value.length === 5,
          }),
        })
        .required(REQUIRED_FIELD_ERROR),
    }),
    place: Yup.string().when('should_fill_with_delivery_data', {
      is: shouldFillWithData => shouldFillWithData === true,
      then: Yup.string(),
      otherwise: Yup.string().required(REQUIRED_FIELD_ERROR),
    }),
    street: Yup.string().when('should_fill_with_delivery_data', {
      is: shouldFillWithData => shouldFillWithData === true,
      then: Yup.string(),
      otherwise: Yup.string().required(REQUIRED_FIELD_ERROR),
    }),
    house_nr: Yup.string(),
    suffix: Yup.string(),
  }),
  roaming: Yup.string()
    .oneOf(['agree', 'disagree'])
    .required(REQUIRED_FIELD_ERROR),
  terms_and_conditions_salt: Yup.bool().oneOf([true], REQUIRED_FIELD_ERROR),
  port_phone_number: Yup.bool().when('is_existing_number', {
    is: is_existing_number => is_existing_number === 'yes',
    then: Yup.bool().oneOf([true], REQUIRED_FIELD_ERROR),
    otherwise: Yup.bool(),
  }),
});

export const formatBirthDate = (
  dateString,
  type = 'yyyy-MM-dd',
  locale = LANGUAGE_DE,
) => {
  const [day, month, year] = dateString.split(' ');
  const date = new Date(year, month - 1, day);

  return format(date, type, { locale: languages[locale] });
};

export const mapFormStateToSend = formValues => {
  const {
    mobile_plan,
    existing_phone_number,
    is_existing_number,
    current_contract_model,
    current_provider,
    gender,
    first_name,
    last_name,
    date_of_birth,
    nationality,
    id_type,
    id_number,
    email,
    contact_phone_number,
    delivery_address,
    billing_address: {
      should_fill_with_delivery_data,
      ...billingAddressValues
    },
    roaming,
    terms_and_conditions_salt,
    port_phone_number,
  } = formValues;

  const billing_address = should_fill_with_delivery_data
    ? {
        gender: parseInt(gender, 10),
        first_name,
        last_name,
        ...delivery_address,
      }
    : {
        ...billingAddressValues,
        gender: parseInt(billingAddressValues.gender, 10),
      };

  return {
    mobile_plan: parseInt(mobile_plan, 10),
    existing_phone_number:
      existing_phone_number?.length > 3 && is_existing_number === 'yes'
        ? existing_phone_number
        : '',
    current_contract_model:
      MOBILE_CONTRACT_ID_MAP[current_contract_model] || null,
    current_provider: parseInt(current_provider, 10) || null,
    gender: parseInt(gender, 10),
    first_name,
    last_name,
    date_of_birth: formatBirthDate(date_of_birth),
    nationality,
    id_type,
    id_number,
    email,
    contact_phone_number,
    delivery_address: {
      first_name,
      last_name,
      ...delivery_address,
    },
    billing_address,
    roaming: roaming === 'agree',
    terms_and_conditions_salt,
    port_phone_number,
  };
};

const deformatBirthDate = dateString => {
  const birthDay = dateString.slice(8, 10);
  const birthMonth = dateString.slice(5, 7);
  const birthYear = dateString.slice(0, 4);

  return `${birthDay} ${birthMonth} ${birthYear}`;
};

const getFirstTruthy = (values, defaultVal = '') =>
  values.find(value => value) || defaultVal;

const getExistingPhoneNumber = (currState, stateNumber) => {
  const { prefix } = extractPhoneParts(stateNumber);

  if (
    currState === initialValues.existing_phone_number &&
    prefix === COUNTRY_PHONE_PREFIX_SWITZERLAND
  ) {
    return stateNumber;
  }

  return currState;
};

export const profileDetailsToFormState = (
  { personal, mobile_plan, phoneData, email },
  currState,
  isStreetNumberAvailable,
) => ({
  ...initialValues,
  ...currState,
  mobile_plan,
  gender:
    (personal.gender && personal.gender.toString()) ||
    currState.gender ||
    initialValues.gender,
  first_name: getFirstTruthy([currState.first_name, personal.firstname]),
  last_name: getFirstTruthy([currState.last_name, personal.lastname]),
  existing_phone_number: getExistingPhoneNumber(
    currState.existing_phone_number,
    phoneData?.phone,
  ),
  date_of_birth:
    (currState.date_of_birth?.replace(/ /g, '')
      ? currState.date_of_birth
      : deformatBirthDate(personal.dateOfBirth)) || '',
  email: getFirstTruthy([currState.email, email]),
  contact_phone_number:
    (currState.contact_phone_number === initialValues.contact_phone_number
      ? phoneData?.phone
      : currState.contact_phone_number) || initialValues.contact_phone_number,
  id_number: getFirstTruthy([currState.id_number, phoneData?.id_number]),
  delivery_address: {
    ...initialValues.delivery_address,
    ...currState.delivery_address,
    country: getFirstTruthy([
      currState.delivery_address?.country,
      personal.address.country,
    ]),
    postal_code: getFirstTruthy([
      currState.delivery_address?.postal_code,
      personal.address.zipCode,
    ]),
    place: getFirstTruthy([
      currState.delivery_address?.place,
      personal.address.city,
    ]),
    street: getFirstTruthy([
      currState.delivery_address?.street,
      personal.address.street,
    ]),
    house_nr: getFirstTruthy([
      currState.delivery_address?.house_nr,
      personal.address.houseNr,
    ]),
    suffix: getFirstTruthy([
      currState.delivery_address?.suffix,
      personal.address.additionalAddress,
    ]),
    noAdditionalStreetNumber: isStreetNumberAvailable,
  },
});

export const validateAboDetailsStep = ({ errors, values }) =>
  !(
    !!errors.mobile_plan ||
    !!errors.existing_phone_number ||
    !!errors.is_existing_number ||
    !!errors.current_contract_model ||
    !!errors.current_provider ||
    values.is_existing_number === ''
  );
